import { useStorage } from '@vueuse/core';
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia';
import { computed, watch } from 'vue';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import { Theme } from '@/types';

export const useThemeStore = defineStore('theme', () => {
  // Persisted fallback for logged-out views (preloader/login)
  const storageTheme = useStorage<Theme>('theme', Theme.Light);

  const userStore = useUserStore();
  const { user } = storeToRefs(userStore);

  const theme = computed<Theme>(() => {
    return (
      (user.value?.settings?.custom?.theme as Theme | undefined) ??
      storageTheme.value
    );
  });

  const setTheme = (theme: Theme) => {
    storageTheme.value = theme; // keep storage in sync for preloader/login
    if (typeof document === 'undefined') return; // SSR safety

    const m = (theme ?? Theme.Light).toLowerCase();
    const body = document.body;

    // no-op if already set
    if (body.classList.contains(`theme-${m}`)) return;

    const applyTheme = () => {
      body.classList.remove('theme-light', 'theme-dark');
      body.classList.add(`theme-${m}`);
    };

    // View Transitions API with correct binding
    if ('startViewTransition' in document) {
      // Call as a method on `document` to avoid "Illegal invocation"
      (document as any).startViewTransition(() => {
        applyTheme();
      });
    } else {
      // Fallback: instant switch
      applyTheme();
    }
  };

  watch(
    theme,
    (val) => {
      setTheme(val);
    },
    { immediate: true },
  );

  const darkerColors = computed(() => {
    // This property makes the colors of events in the calendar slightly darker
    const { companySettings } = useCompanyStore();
    return theme.value === Theme.Dark || companySettings.agenda?.darkerColors;
  });

  const primaryColor = computed(() => '#4c49c5');

  return { theme, darkerColors, primaryColor, setTheme };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useThemeStore, import.meta.hot));
}
